import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';
import useFormState from '../../hooks/useFormState.js';

const CLASS_TYPES = ['ASD', 'Conventional', 'Voith', 'Tractor', 'RotorTug'];
const STATUSES = ['Available', 'In-Job', 'Maintenance', 'Out of Service'];

const EMPTY_TUG = {
  id: null,
  name: '',
  classType: 'ASD',
  bollardPullTons: '',
  homeStationId: '',
  trackingDeviceId: '',
  fuelCapacityL: '',
  contactNumber: '',
  operationalStatus: 'Available',
};

function statusBadge(status) {
  switch (status) {
    case 'Available':
      return 'bg-success';
    case 'In-Job':
      return 'bg-info text-dark';
    case 'Maintenance':
      return 'bg-warning text-dark';
    default:
      return 'bg-secondary';
  }
}

export default function OwnedTugsPage() {
  const [tugs, setTugs] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pageMsg, setPageMsg] = useState(null);

  // Register/Edit modal.
  const [modalOpen, setModalOpen] = useState(false);
  const {
    form,
    setForm,
    errors: formErrors,
    setErrors: setFormErrors,
    setField,
  } = useFormState(EMPTY_TUG);
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    // Stations are master data (admin-read); a Dispatcher may lack geography
    // read, so degrade gracefully rather than failing the whole page load.
    const [tugsRes, stationsRes] = await Promise.all([
      api.listTugs(),
      api.listStations().catch(() => []),
    ]);
    setTugs(tugsRes || []);
    setStations(stationsRes || []);
    return tugsRes || [];
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await loadAll();
      } catch (err) {
        setLoadError(err.message || 'Failed to load owned tugs.');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadAll]);

  const openRegister = () => {
    setFormErrors({});
    setForm(EMPTY_TUG);
    setModalOpen(true);
  };

  const openEdit = (tug) => {
    setFormErrors({});
    setForm({
      id: tug.id,
      name: tug.name,
      classType: tug.class_type,
      bollardPullTons: String(tug.bollard_pull_tons ?? ''),
      homeStationId: tug.home_station_id || '',
      trackingDeviceId: tug.tracking_device_id || '',
      fuelCapacityL: tug.fuel_capacity_l != null ? String(Number(tug.fuel_capacity_l)) : '',
      contactNumber: tug.contact_number || '',
      operationalStatus: tug.operational_status,
    });
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'Tug name is required';
    if (!form.classType) next.classType = 'Class/Type is required';
    const bp = Number(form.bollardPullTons);
    if (!form.bollardPullTons || Number.isNaN(bp) || bp <= 0)
      next.bollardPullTons = 'Bollard pull must be a positive number';
    if (!STATUSES.includes(form.operationalStatus)) next.operationalStatus = 'Select a status';
    setFormErrors(next);
    return Object.keys(next).length === 0;
  };

  const saveTug = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setPageMsg(null);
    try {
      const payload = {
        name: form.name.trim(),
        classType: form.classType,
        bollardPullTons: Number(form.bollardPullTons),
        homeStationId: form.homeStationId || null,
        trackingDeviceId: form.trackingDeviceId.trim() || null,
        fuelCapacityL: form.fuelCapacityL === '' ? null : Number(form.fuelCapacityL),
        contactNumber: form.contactNumber.trim() || null,
        operationalStatus: form.operationalStatus,
      };
      if (form.id) await api.updateTug(form.id, payload);
      else await api.createTug(payload);
      setPageMsg({ type: 'success', text: 'Owned tug saved successfully.' });
      await loadAll();
      setModalOpen(false);
    } catch (err) {
      setFormErrors((p) => ({ ...p, form: err.message || 'Failed to save tug.' }));
    } finally {
      setSaving(false);
    }
  };

  const removeTug = async (tug) => {
    setPageMsg(null);
    try {
      await api.deleteTug(tug.id);
      await loadAll();
      setPageMsg({ type: 'success', text: `${tug.name} removed.` });
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to delete tug.' });
    }
  };

  return (
    <div className="container-fluid py-4" data-testid="owned-tugs-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-ship me-2" aria-hidden="true" />
                Owned Tug Fleet Registry
              </h3>
              <p className="text-muted mb-0 small">
                Manage owned tug assets, bollard pull capacities, home stations, and tracking devices.
                Maintenance &amp; bunkering windows are scheduled on their own page.
              </p>
            </div>
            <button className="btn btn-primary" onClick={openRegister} data-testid="register-tug">
              <i className="fas fa-plus me-1" aria-hidden="true" />
              Register New Tug
            </button>
          </div>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" data-testid="tugs-load-error">
          {loadError}
        </div>
      )}
      {pageMsg && (
        <div className={`alert alert-${pageMsg.type}`} role="alert" data-testid="tug-message">
          {pageMsg.text}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <div className="row">
          {/* Fleet registry (maintenance/bunkering now lives on its own page). */}
          <div className="col-12 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-list-ul me-2" aria-hidden="true" />
                  Owned Fleet Registry
                </h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light text-secondary small text-uppercase">
                      <tr>
                        <th>Tug Name</th>
                        <th>Class / Type</th>
                        <th>Bollard Pull</th>
                        <th>Home Station</th>
                        <th>Status</th>
                        <th aria-label="actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {tugs.length === 0 && (
                        <tr>
                          <td colSpan={6} className="text-center text-muted py-4">
                            No owned tugs registered yet.
                          </td>
                        </tr>
                      )}
                      {tugs.map((t) => (
                        <tr key={t.id} data-testid="tug-row" data-name={t.name}>
                          <td className="fw-bold text-dark">
                            {t.name}
                            {t.tracking_device_id ? (
                              <span className="d-block text-muted small">
                                <i className="fas fa-satellite-dish me-1" aria-hidden="true" />
                                {t.tracking_device_id}
                              </span>
                            ) : null}
                          </td>
                          <td>{t.class_type}</td>
                          <td>
                            <span data-testid="tug-bollard">{Number(t.bollard_pull_tons)}</span> T
                          </td>
                          <td className="small">{t.home_station_name || '—'}</td>
                          <td>
                            <span className={`badge ${statusBadge(t.operational_status)}`} data-testid="tug-status">
                              {t.operational_status}
                            </span>
                          </td>
                          <td className="text-end">
                            <button
                              className="btn btn-sm btn-outline-secondary me-1"
                              onClick={() => openEdit(t)}
                              data-testid="edit-tug"
                              aria-label={`Edit ${t.name}`}
                            >
                              <i className="fas fa-pen" aria-hidden="true" />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeTug(t)}
                              aria-label={`Delete ${t.name}`}
                            >
                              <i className="fas fa-trash" aria-hidden="true" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Register / Edit modal */}
      {modalOpen && (
        <>
          <div className="modal d-block" tabIndex={-1} role="dialog" data-testid="tug-modal">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <form onSubmit={saveTug} noValidate>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="fas fa-ship me-2" aria-hidden="true" />
                      {form.id ? 'Edit Owned Tug' : 'Register New Tug'}
                    </h5>
                    <button type="button" className="btn-close" aria-label="Close" onClick={closeModal} />
                  </div>
                  <div className="modal-body">
                    {formErrors.form && <div className="alert alert-danger">{formErrors.form}</div>}
                    <div className="mb-3">
                      <label htmlFor="tug-name" className="form-label small fw-bold text-muted">
                        Tug Name <span className="text-danger">*</span>
                      </label>
                      <input
                        id="tug-name"
                        className={`form-control form-control-sm${formErrors.name ? ' is-invalid' : ''}`}
                        value={form.name}
                        onChange={(e) => setField('name', e.target.value)}
                        data-testid="tug-name"
                      />
                      {formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
                    </div>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="tug-class" className="form-label small fw-bold text-muted">
                          Class / Type <span className="text-danger">*</span>
                        </label>
                        <select
                          id="tug-class"
                          className="form-select form-select-sm"
                          value={form.classType}
                          onChange={(e) => setField('classType', e.target.value)}
                          data-testid="tug-class"
                        >
                          {CLASS_TYPES.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label htmlFor="tug-bollard" className="form-label small fw-bold text-muted">
                          Bollard Pull (Tons) <span className="text-danger">*</span>
                        </label>
                        <input
                          id="tug-bollard"
                          type="number"
                          step="0.1"
                          className={`form-control form-control-sm${formErrors.bollardPullTons ? ' is-invalid' : ''}`}
                          value={form.bollardPullTons}
                          onChange={(e) => setField('bollardPullTons', e.target.value)}
                          data-testid="tug-bollard-input"
                        />
                        {formErrors.bollardPullTons && (
                          <div className="invalid-feedback">{formErrors.bollardPullTons}</div>
                        )}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label htmlFor="tug-station" className="form-label small fw-bold text-muted">
                        Home Station
                      </label>
                      <select
                        id="tug-station"
                        className="form-select form-select-sm"
                        value={form.homeStationId}
                        onChange={(e) => setField('homeStationId', e.target.value)}
                        data-testid="tug-station"
                      >
                        <option value="">— None —</option>
                        {stations.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-3">
                      <label htmlFor="tug-tracking" className="form-label small fw-bold text-muted">
                        Tracking Device ID
                      </label>
                      <input
                        id="tug-tracking"
                        className="form-control form-control-sm"
                        value={form.trackingDeviceId}
                        onChange={(e) => setField('trackingDeviceId', e.target.value)}
                        data-testid="tug-tracking"
                      />
                    </div>
                    <div className="row">
                      <div className="col-6 mb-3">
                        <label htmlFor="tug-capacity" className="form-label small fw-bold text-muted">
                          Fuel Capacity (L)
                        </label>
                        <input
                          id="tug-capacity"
                          type="number"
                          min="0"
                          className="form-control form-control-sm"
                          value={form.fuelCapacityL}
                          onChange={(e) => setField('fuelCapacityL', e.target.value)}
                          data-testid="tug-capacity"
                        />
                      </div>
                      <div className="col-6 mb-3">
                        <label htmlFor="tug-contact" className="form-label small fw-bold text-muted">
                          Contact Number
                        </label>
                        <input
                          id="tug-contact"
                          className="form-control form-control-sm"
                          maxLength={40}
                          value={form.contactNumber}
                          onChange={(e) => setField('contactNumber', e.target.value)}
                          data-testid="tug-contact"
                        />
                      </div>
                    </div>
                    <div className="mb-1">
                      <label htmlFor="tug-status" className="form-label small fw-bold text-muted">
                        Operational Status
                      </label>
                      <select
                        id="tug-status"
                        className="form-select form-select-sm"
                        value={form.operationalStatus}
                        onChange={(e) => setField('operationalStatus', e.target.value)}
                        data-testid="tug-status-select"
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={closeModal}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={saving} data-testid="save-tug">
                      {saving && (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      )}
                      <i className="fas fa-save me-1" aria-hidden="true" />
                      Save
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
}
